import { useState, useCallback, useEffect } from "react";
import { getJobs, createJob, updateJob, deleteJob } from "@/api/jobs";
import { JobHubJob, transformJobForDisplay } from "@/lib/jobUtils";
import { JobStatus, JobPriority, UserRole } from "@/lib/schema";
import { User as FirebaseUser } from "firebase/auth";
import { User } from "@/lib/schema";

interface UseJobOperationsProps {
  userData: User | null;
  currentUser: FirebaseUser | null;
}

export const useJobOperations = ({ userData, currentUser }: UseJobOperationsProps) => {
  const [jobsData, setJobsData] = useState<JobHubJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!userData || !currentUser) {
        return;
      }

      let filters: any = {};
      
      // Role-based filtering
      if (userData.role === UserRole.RECRUITER) {
        filters.recruiterId = currentUser.uid;
      }

      const jobs = await getJobs(filters);
      
      const transformedJobs: JobHubJob[] = jobs.map(transformJobForDisplay);
      
      setJobsData(transformedJobs);
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError("Failed to load jobs. Please try again.");
    } finally {
      setLoading(false);
      setHasAttemptedFetch(true);
    }
  }, [userData, currentUser]);

  const handleBulkAction = useCallback(async (action: string, selectedJobIds: string[]) => {
    try {
      
      const promises = selectedJobIds.map(async (jobId) => {
        if (action === "activate") {
          return updateJob(jobId, { status: JobStatus.ACTIVE });
        } else if (action === "pause") {
          return updateJob(jobId, { status: JobStatus.DRAFT });
        } else if (action === "delete") {
          return deleteJob(jobId);
        }
      });
      
      await Promise.all(promises);
      await fetchJobs();
    } catch (err) {
      console.error("Error performing bulk action:", err);
      setError("Failed to perform bulk action. Please try again.");
    }
  }, [fetchJobs]);

  const handleJobStatusChange = useCallback(async (jobId: string, action: 'pause' | 'activate') => {
    try {
      const newStatus = action === 'pause' ? JobStatus.DRAFT : JobStatus.ACTIVE;
      await updateJob(jobId, { status: newStatus });
      await fetchJobs();
    } catch (err) {
      console.error("Error updating job status:", err);
      setError("Failed to update job status. Please try again.");
    }
  }, [fetchJobs]);

  const handleCreateJob = useCallback(async (jobData: any) => {
    try {
      if (!currentUser) {
        setError("User not authenticated");
        return false;
      }

      const formattedJobData = {
        ...jobData,
        status: jobData.status as JobStatus,
        priority: jobData.priority as JobPriority,
        applicants: 0,
        recruiterId: jobData.assignedRecruiter === "unassigned" ? currentUser.uid : jobData.assignedRecruiter
      };
      
      await createJob(formattedJobData);
      await fetchJobs();
      return true;
    } catch (err) {
      console.error("Error creating job:", err);
      setError("Failed to create job. Please try again.");
      return false;
    }
  }, [currentUser, fetchJobs]);

  const handleUpdateJob = useCallback(async (jobId: string, updates: any) => {
    try {
      const formattedUpdates = {
        ...updates,
        status: updates.status as JobStatus,
        priority: updates.priority as JobPriority,
      };
      
      await updateJob(jobId, formattedUpdates);
      await fetchJobs();
      return true;
    } catch (err) {
      console.error("Error updating job:", err);
      setError("Failed to update job. Please try again.");
      return false;
    }
  }, [fetchJobs]);

  const clearError = useCallback(() => setError(null), []);

  // Auto-fetch jobs when user data is available (only once)
  useEffect(() => {
    if (userData && currentUser && !hasAttemptedFetch) {
      setHasAttemptedFetch(true);
      fetchJobs();
    }
  }, [userData, currentUser, hasAttemptedFetch, fetchJobs]);

  return {
    jobsData,
    loading,
    error,
    fetchJobs,
    handleBulkAction,
    handleJobStatusChange,
    handleCreateJob,
    handleUpdateJob,
    clearError
  };
};
